import { useState } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft, Loader2, ImageIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useOrders } from '../../hooks/useOrders';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useTables } from '../../hooks/useTables';
import { useCurrency } from '../../hooks/useCurrency';
import { useVAT } from '../../hooks/useVAT';
import { ordersService } from '../../services/orders.service';
import { notificationsService } from '../../services/notifications.service';
import { toast } from 'sonner';

interface CartPageProps {
  onNavigate?: (page: string) => void;
}

export function CartPage({ onNavigate }: CartPageProps) {
  // Default to closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const { total: ordersTotal } = useOrders({ page: 1, limit: 50 });
  const { cart, addToCart, removeFromCart, getTotalItems, clearCart } = useCart();
  const { orderByTable, selectedTable, currentBranch } = useRestaurant();
  const { tables } = useTables();
  const { formatCurrency, formatCurrencySpaced } = useCurrency();
  const { calculateOrderVAT, getVATInfo } = useVAT();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the selected table name
  const selectedTableData = tables.find(table => table.id === selectedTable);
  const tableName = selectedTableData?.name || '';

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty!');
      return;
    }

    if (!currentBranch) {
      toast.error('Please select a branch first');
      return;
    }

    if (orderByTable && !selectedTable) {
      toast.error('Please select a table first');
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate totals using VAT hook
      const cartItems = cart.map(item => ({
        price: item.price,
        quantity: item.quantity,
      }));
      const vatCalculation = calculateOrderVAT(cartItems);
      const subtotal = vatCalculation.subtotal;
      const tax = vatCalculation.vatAmount;
      const total = vatCalculation.total;

      console.log('🔵 [CART] VAT Calculation:', {
        vatInclusive: getVATInfo().mode,
        vatRate: getVATInfo().rate,
        cartItems,
        vatCalculation,
        subtotal,
        tax,
        total
      });

      console.log('🔵 [CART] Starting order creation...');
      console.log('🔵 [CART] Current Branch:', currentBranch);
      console.log('🔵 [CART] Cart Items:', cart);
      console.log('🔵 [CART] Selected Table:', selectedTable, tableName);

      // Prepare order data
      const orderData = {
        tableId: orderByTable ? (selectedTable ?? undefined) : undefined,
        items: cart.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity, // Line item total = price × quantity (before any item-level discount)
          discount: 0, // Item-level discount (if you add discount per item in the future)
          notes: '',
        })),
        subtotal: subtotal,
        tax: tax,
        total: total,
        notes: orderByTable && selectedTable ? `Table: ${tableName}` : '',
        paymentMethod: 'CASH',  // Use valid payment method
        discount: 0,
      };

      console.log('🔵 [CART] Order Data:', JSON.stringify(orderData, null, 2));

      // Create the order
      console.log('🔵 [CART] Calling ordersService.createOrder...');
      const response = await ordersService.createOrder(orderData);
      console.log('🔵 [CART] Order Response:', response);

      if (response.success && response.data) {
        console.log('🟢 [CART] Order created successfully:', response.data);

        // Create a notification for the new order
        try {
          const notificationData = {
            title: '🍽️ New Order',
            message: orderByTable && tableName 
              ? `New order from ${tableName} - ${getTotalItems()} items - ${formatCurrency(total)}`
              : `New order - ${getTotalItems()} items - ${formatCurrency(total)}`,
            type: 'ORDER' as const,
            metadata: {
              orderId: response.data.id,
              orderNumber: response.data.orderNumber,
              tableId: selectedTable,
              tableName: tableName,
              itemCount: getTotalItems(),
              total: total,
            },
          };

          console.log('🔵 [CART] Creating notification:', notificationData);
          await notificationsService.createNotification(notificationData);
          console.log('🟢 [CART] Notification created successfully');
        } catch (notifError) {
          console.warn('⚠️ [CART] Failed to create notification (non-critical):', notifError);
          // Continue even if notification fails
        }

        // Clear the cart
        clearCart();

        // Show success message
        toast.success(`✅ Order sent to kitchen successfully!\nOrder #${response.data.orderNumber}\nTotal: ${formatCurrency(total)}`);

        // Navigate to orders page
        onNavigate?.('orders');
      } else {
        console.error('🔴 [CART] Order creation failed - no data in response:', response);
        throw new Error('Order creation failed - no data returned');
      }
    } catch (error: any) {
      console.error('🔴 [CART] Failed to create order - Full Error:', error);
      console.error('🔴 [CART] Error message:', error?.message);
      console.error('🔴 [CART] Error response:', error?.response);
      console.error('🔴 [CART] Error data:', error?.response?.data);
      
      // More detailed error message
      let errorMessage = '❌ Failed to send order to kitchen.\n\n';
      if (error?.response?.data?.message) {
        errorMessage += `Error: ${error.response.data.message}`;
      } else if (error?.message) {
        errorMessage += `Error: ${error.message}`;
      } else {
        errorMessage += 'Please check the console for details.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="cart"
        onNavigate={onNavigate}
        ordersCount={ordersTotal || 0}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => onNavigate?.('menus')}
                  className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {orderByTable && tableName ? `Checkout - ${tableName}` : 'Checkout'}
                  </h1>
                  <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">
                    {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'} in your cart
                  </p>
                </div>
              </div>
              
              {cart.length > 0 && (
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to clear your cart?')) {
                      clearCart();
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Clear Cart</span>
                </button>
              )}
            </div>

            {cart.length === 0 ? (
              /* Empty State */
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Your cart is empty</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Add some delicious items from our menu to get started
                </p>
                <button
                  onClick={() => onNavigate?.('menus')}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                >
                  Browse Menu
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Cart Items */}
                <div className="lg:col-span-2 space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-4">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-24 h-24 object-cover rounded-xl"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{item.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                            {item.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => removeFromCart(item.id)}
                                className="p-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                              >
                                <Minus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                              </button>
                              <span className="text-base font-semibold text-gray-900 dark:text-white w-8 text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => addToCart(item)}
                                className="p-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                              >
                                <Plus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                              </button>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                                {formatCurrencySpaced(item.price)} each
                              </p>
                              <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                                {formatCurrencySpaced(item.price * item.quantity)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 sticky top-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Summary</h2>
                    
                    <div className="space-y-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
                      {getVATInfo().mode === 'Inclusive' ? (
                        // Inclusive: Amount (Excl. VAT) + VAT = Items Total
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Amount (Excl. VAT)</span>
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                              {formatCurrencySpaced(calculateOrderVAT(cart.map(item => ({ price: item.price, quantity: item.quantity }))).subtotal)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">VAT {getVATInfo().rate} (Included)</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatCurrencySpaced(calculateOrderVAT(cart.map(item => ({ price: item.price, quantity: item.quantity }))).vatAmount)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Items Total</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatCurrencySpaced(calculateOrderVAT(cart.map(item => ({ price: item.price, quantity: item.quantity }))).total)}
                            </span>
                          </div>
                        </>
                      ) : (
                        // Exclusive: Subtotal + VAT = Total
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Subtotal ({getTotalItems()} items)</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatCurrencySpaced(calculateOrderVAT(cart.map(item => ({ price: item.price, quantity: item.quantity }))).subtotal)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">VAT {getVATInfo().rate}</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatCurrencySpaced(calculateOrderVAT(cart.map(item => ({ price: item.price, quantity: item.quantity }))).vatAmount)}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex items-start gap-1 text-xs text-gray-500 dark:text-gray-400 pt-1">
                        <span>ℹ️</span>
                        <span>{getVATInfo().description}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-6">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">Total to Pay</span>
                      <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {formatCurrencySpaced(calculateOrderVAT(cart.map(item => ({ price: item.price, quantity: item.quantity }))).total)}
                      </span>
                    </div>

                    <button
                      onClick={handleCheckout}
                      disabled={isSubmitting}
                      className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold mb-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          🍳 Send to Kitchen
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => onNavigate?.('menus')}
                      className="w-full py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-semibold"
                    >
                      Add More Items
                    </button>

                    {/* Delivery Info */}
                    <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
                      <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-300 mb-2">
                        🚚 Delivery Information
                      </h3>
                      <p className="text-xs text-indigo-700 dark:text-indigo-400">
                        Estimated delivery time: 30-45 minutes
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}